// CreateWorkspaceModal.tsx — orquestador del onboarding conversacional de Novah.
// Qué hace: máquina de steps tipo chat para crear un workspace (scan web / manual
//   / documento). Mantiene el estado compartido, los handlers de transición y el
//   shell de chat; delega cada step a components/createWorkspace/steps/* y la
//   lógica pesada a hooks/createWorkspace/* (scan SSE y creación del workspace).
// Qué exporta: CreateWorkspaceModal (mismas props y comportamiento que antes).
// Quién lo usa: DashboardPage.

import { useState, useRef, useEffect, useCallback } from 'react';
import { Workspace, TemplateSummary } from '@agency-smm/shared';
import { Modal } from './Modal';
import { AiLogoGenerator } from './AiLogoGenerator';
import { useUrlScan } from '../hooks/createWorkspace/useUrlScan';
import { useCreateWorkspace } from '../hooks/createWorkspace/useCreateWorkspace';
import { WizardStep, ContentMix, ScanResult, ChatMsg } from './createWorkspace/types';
import { InputArea, BackBtn } from './createWorkspace/styled';
import { ChatBubbles } from './createWorkspace/ChatBubble';
import { SourceChoiceStep } from './createWorkspace/steps/SourceChoiceStep';
import { UrlInputStep } from './createWorkspace/steps/UrlInputStep';
import { BrandReviewStep } from './createWorkspace/steps/BrandReviewStep';
import { BrandNameStep } from './createWorkspace/steps/BrandNameStep';
import { FontPreviewStep } from './createWorkspace/steps/FontPreviewStep';
import { LogoSelectStep } from './createWorkspace/steps/LogoSelectStep';
import { LogoAltStep } from './createWorkspace/steps/LogoAltStep';
import { PhotoStep } from './createWorkspace/steps/PhotoStep';
import { TemplateSelectStep } from './createWorkspace/steps/TemplateSelectStep';
import { ContentMixStep } from './createWorkspace/steps/ContentMixStep';
import { ManualNameStep } from './createWorkspace/steps/ManualNameStep';
import { ManualLogoStep } from './createWorkspace/steps/ManualLogoStep';
import { DocumentStep } from './createWorkspace/steps/DocumentStep';
import { DoneStep } from './createWorkspace/steps/DoneStep';
import { GreetingStep, ScanningStep, CreatingStep, DocumentAnalyzingStep } from './createWorkspace/steps/StatusSteps';
import { AiLoadingOverlay } from './AiLoadingOverlay';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (workspace: Workspace) => void;
  inline?: boolean;
}

export function CreateWorkspaceModal({ isOpen, onClose, onCreated, inline }: Props) {
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [step, setStep] = useState<WizardStep>('greeting');
  const [stepHistory, setStepHistory] = useState<WizardStep[]>([]);
  const [typing, setTyping] = useState(false);
  const [url, setUrl] = useState('');
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const scanResultRef = useRef<ScanResult | null>(null);
  const [brandName, setBrandName] = useState('');
  const [selectedLogo, setSelectedLogo] = useState<string | null>(null);
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set());
  const [globalTemplates, setGlobalTemplates] = useState<TemplateSummary[]>([]);
  const [contentMix, setContentMix] = useState<ContentMix>({ education: 25, connection: 25, proof: 25, promotion: 25 });
  const [checkedCategories, setCheckedCategories] = useState<Set<keyof ContentMix>>(new Set(['education', 'connection', 'proof', 'promotion']));

  const toggleCategory = (cat: keyof ContentMix) => {
    setCheckedCategories(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      if (next.size === 0) return prev; // prevent unchecking all
      // Auto-balance
      const cats = Array.from(next);
      const base = Math.floor(100 / cats.length);
      const remainder = 100 - base * cats.length;
      const newMix: ContentMix = { education: 0, connection: 0, proof: 0, promotion: 0 };
      cats.forEach((c, i) => { newMix[c] = base + (i < remainder ? 1 : 0); });
      setContentMix(newMix);
      return next;
    });
  };
  const [isCreating, setIsCreating] = useState(false);
  const [manualName, setManualName] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  // Variantes de logo IA NO elegidas → se guardan como brand assets extra.
  const [extraLogoUrls, setExtraLogoUrls] = useState<string[]>([]);
  const [docFile, setDocFile] = useState<File | null>(null);
  const docFileRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoUrlInput, setLogoUrlInput] = useState('');
  // Desde qué flujo se entró al step 'ai-logo' (define la continuación al terminar).
  const [aiLogoOrigin, setAiLogoOrigin] = useState<'scan' | 'manual'>('scan');
  const [selectedFont, setSelectedFont] = useState<string | null>(null);
  const [uploadedPhotos, setUploadedPhotos] = useState<File[]>([]);

  // Brand review editable fields
  const [reviewDescription, setReviewDescription] = useState('');
  const [reviewIndustry, setReviewIndustry] = useState('');
  const [reviewPhone, setReviewPhone] = useState('');
  const [reviewEmail, setReviewEmail] = useState('');
  const [reviewWebsite, setReviewWebsite] = useState('');
  const [reviewColors, setReviewColors] = useState<string[]>([]);
  const [colorInput, setColorInput] = useState('#3B82F6');
  const [photoUrlInput, setPhotoUrlInput] = useState('');
  const photoFileRef = useRef<HTMLInputElement>(null);
  const [createdWs, setCreatedWs] = useState<Workspace | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const msgIdRef = useRef(0);
  const analyzeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const nextId = () => ++msgIdRef.current;

  const pushStep = (next: WizardStep) => {
    setStepHistory(prev => [...prev, step]);
    setStep(next);
  };

  const goBack = () => {
    if (stepHistory.length === 0) return;
    const prev = stepHistory[stepHistory.length - 1];
    setStepHistory(h => h.slice(0, -1));
    setStep(prev);
  };

  const scroll = useCallback(() => {
    setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  const addMsg = useCallback((msg: Omit<ChatMsg, 'id'>) => {
    setMessages(prev => [...prev, { ...msg, id: nextId() }]);
    scroll();
  }, [scroll]);

  const novah = useCallback((text: string, extra?: Partial<ChatMsg>) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      addMsg({ from: 'novah', text, ...extra });
    }, 400);
  }, [addMsg]);

  const user = useCallback((text: string) => {
    addMsg({ from: 'user', text });
  }, [addMsg]);

  // Reset
  const reset = () => {
    setMessages([]);
    setStep('source-choice');
    setStepHistory([]);
    setTyping(false);
    setUrl('');
    setScanResult(null);
    setBrandName('');
    setSelectedLogo(null);
    setSelectedPhotos(new Set());
    setSelectedTemplates(new Set());
    setGlobalTemplates([]);
    setContentMix({ education: 40, connection: 30, proof: 20, promotion: 10 });
    setIsCreating(false);
    setManualName('');
    setLogoFile(null);
    setLogoPreview('');
    setLogoUrlInput('');
    setSelectedFont(null);
    setUploadedPhotos([]);
    setPhotoUrlInput('');
    setCreatedWs(null);
    msgIdRef.current = 0;
    if (analyzeTimeoutRef.current) { clearTimeout(analyzeTimeoutRef.current); analyzeTimeoutRef.current = null; }
  };

  // Init greeting — explain what we're doing and offer options
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setTimeout(() => {
        addMsg({ from: 'novah', text: "Hi! I'm Novah, your brand assistant. First, I need to learn about your brand — your voice, style, colors, and what you do." });
      }, 300);
      setTimeout(() => {
        addMsg({ from: 'novah', text: "How would you like to share your brand info?" });
        setStep('source-choice');
      }, 1000);
    }
  }, [isOpen, messages.length, addMsg]);

  // Load detected fonts when entering font-preview step
  useEffect(() => {
    if (step === 'font-preview') {
      const fonts = scanResultRef.current?.brandFonts || scanResult?.brandFonts || [];
      fonts.forEach(font => {
        import('../utils/fontLoader').then(m => m.ensureFontLoaded(font));
      });
    }
  }, [step, scanResult]);

  const handleClose = () => { onClose(); reset(); };

  // ─── Hooks pesados (scan SSE + creación) ───

  const { handleCreate } = useCreateWorkspace({
    scanResult, brandName, manualName, selectedLogo, logoFile, extraLogoUrls, selectedPhotos,
    selectedTemplates, selectedFont, contentMix, setStep, setIsCreating, setCreatedWs, addMsg,
  });

  const { handleScan } = useUrlScan({
    url, setUrl, brandName, setBrandName, scanResultRef, setScanResult,
    setStep, pushStep, user, addMsg, analyzeTimeoutRef,
  });

  // ─── Step handlers ───

  const goToTemplates = () => {
    // Templates and content strategy are now selected at generation time, not during onboarding
    // Skip straight to workspace creation
    handleCreate();
  };

  const confirmTemplates = () => {
    // Legacy — no longer used in onboarding
    handleCreate();
  };

  const confirmBrandName = () => {
    const name = brandName.trim() || manualName.trim();
    if (!name) return;
    user(`Brand name: ${name}`);
    const fonts = scanResultRef.current?.brandFonts || scanResult?.brandFonts || [];
    if (fonts.length > 0) {
      novah("I found these fonts on your website. Pick one for your templates, or skip to use defaults.");
      pushStep('font-preview');
    } else {
      goToTemplates();
    }
  };

  const confirmFont = () => {
    if (selectedFont) {
      user(`Font: ${selectedFont}`);
    } else {
      user('Using default fonts');
    }
    goToTemplates();
  };

  const confirmLogo = () => {
    user(selectedLogo ? 'Logo selected ✓' : 'Skipped logo');
    const scanPhotos = allImages.filter(i => i.url !== selectedLogo);
    if (scanPhotos.length > 0) {
      novah(`I found ${scanPhotos.length} images on your site. Select the ones you want, upload your own, or skip.`);
    } else {
      novah("Want to add background images for your posts? Upload files, paste URLs, or skip.");
    }
    pushStep('photo-choice');
  };

  // El logo de IA llega YA recortado (PNG transparente) como File. Lo tratamos como
  // file pending: selectedLogo = objectURL (blob:) → handleCreate lo sube con
  // isLogo=true. Luego continuamos según el flujo de origen.
  const handleAiLogoReady = (file: File, previewUrl: string, extras: string[] = []) => {
    setLogoFile(file);
    setLogoPreview(previewUrl);
    setSelectedLogo(previewUrl);
    setExtraLogoUrls(extras);
    user('AI logo created ✓');
    if (aiLogoOrigin === 'manual') {
      goToTemplates();
    } else {
      const scanPhotos = allImages.filter(i => i.url !== previewUrl);
      if (scanPhotos.length > 0) {
        novah(`I found ${scanPhotos.length} images on your site. Select the ones you want, upload your own, or skip.`);
      } else {
        novah("Want to add background images for your posts? Upload files, paste URLs, or skip.");
      }
      pushStep('photo-choice');
    }
  };

  const goToBrandName = () => {
    novah(`I detected your brand name as **${brandName || 'Unknown'}**. Is this correct? Edit below if needed.`);
    pushStep('brand-name');
  };

  const confirmPhotos = () => {
    const totalPhotos = selectedPhotos.size + uploadedPhotos.length;
    user(totalPhotos > 0 ? `${totalPhotos} background image${totalPhotos > 1 ? 's' : ''} selected` : 'No backgrounds selected');
    goToBrandReview();
  };

  const goToBrandReview = () => {
    // Pre-fill review fields from scan result
    const info = scanResultRef.current?.brandInfo || scanResult?.brandInfo || {};
    setReviewDescription((info.description as string) || (info.businessOverview as string) || '');
    setReviewIndustry((info.productsServices as string[])?.join(', ') || '');
    setReviewPhone((info.phoneNumber as string) || '');
    setReviewEmail((info.email as string) || '');
    setReviewWebsite(scanResultRef.current?.url || scanResult?.url || '');
    const scannedColors = scanResultRef.current?.brandColors || scanResult?.brandColors || [];
    setReviewColors(scannedColors.slice(0, 6));
    novah("Here's what I found about your brand. Review and edit anything — especially your brand colors. I'll use them to style your ads!");
    pushStep('brand-review');
  };

  const confirmBrandReview = () => {
    // Save edited fields back to scanResult.brandInfo
    const info = { ...(scanResultRef.current?.brandInfo || scanResult?.brandInfo || {}) };
    if (reviewDescription) info.description = reviewDescription;
    if (reviewIndustry) info.productsServices = reviewIndustry.split(',').map(s => s.trim()).filter(Boolean);
    if (reviewPhone) info.phoneNumber = reviewPhone;
    if (reviewEmail) info.email = reviewEmail;
    if (reviewWebsite) info.sourceUrl = reviewWebsite;

    // Update scan result ref with colors
    if (scanResultRef.current) {
      scanResultRef.current = { ...scanResultRef.current, brandInfo: info, brandColors: reviewColors };
      setScanResult(scanResultRef.current);
    }

    user(`Brand info confirmed ✓${reviewColors.length ? ` · ${reviewColors.length} color${reviewColors.length > 1 ? 's' : ''}` : ''}`);
    goToBrandName();
  };

  const addPhotoUrl = () => {
    const trimmed = photoUrlInput.trim();
    if (!trimmed) return;
    setSelectedPhotos(prev => new Set([...prev, trimmed]));
    setPhotoUrlInput('');
    user(`Added: ${trimmed}`);
    novah('Got it! Add more or continue.');
  };

  // ─── Render ───

  // Use ref as primary source (populated synchronously during SSE), fall back to state
  const allImages = scanResultRef.current?.images || scanResult?.images || [];

  const chatContent = (
    <>
      {/* Pantalla de carga animada durante las esperas IA del onboarding
          (scan de la web / análisis del documento). Overlay fixed: queda por
          encima del modal. */}
      <AiLoadingOverlay
        open={step === 'scanning' || step === 'document-analyzing'}
        kicker="AgencySMM"
        title={step === 'document-analyzing' ? 'Reading your document…' : 'Scanning your brand…'}
        subtitle={step === 'document-analyzing'
          ? 'Extracting your brand signals from the document.'
          : 'Crawling the site for colors, type, voice and key info.'}
        steps={[
          'Fetching your source',
          'Reading colors & fonts',
          'Extracting brand voice',
          'Building your profile',
        ]}
      />

      <ChatBubbles messages={messages} typing={typing} scrollRef={scrollRef} />

      {/* ─── Interactive area ─── */}
      <InputArea>
        {step === 'source-choice' && (
          <SourceChoiceStep user={user} novah={novah} pushStep={pushStep} />
        )}

        {step === 'greeting' && <GreetingStep />}

        {step === 'url-input' && (
          <UrlInputStep url={url} setUrl={setUrl} handleScan={handleScan} />
        )}

        {step === 'scanning' && <ScanningStep />}

        {step === 'brand-review' && (
          <BrandReviewStep
            reviewDescription={reviewDescription} setReviewDescription={setReviewDescription}
            reviewIndustry={reviewIndustry} setReviewIndustry={setReviewIndustry}
            reviewPhone={reviewPhone} setReviewPhone={setReviewPhone}
            reviewEmail={reviewEmail} setReviewEmail={setReviewEmail}
            reviewWebsite={reviewWebsite} setReviewWebsite={setReviewWebsite}
            reviewColors={reviewColors} setReviewColors={setReviewColors}
            colorInput={colorInput} setColorInput={setColorInput}
            confirmBrandReview={confirmBrandReview}
          />
        )}

        {step === 'brand-name' && (
          <BrandNameStep brandName={brandName} setBrandName={setBrandName} confirmBrandName={confirmBrandName} />
        )}

        {step === 'font-preview' && (
          <FontPreviewStep
            fonts={scanResultRef.current?.brandFonts || scanResult?.brandFonts || []}
            selectedFont={selectedFont} setSelectedFont={setSelectedFont} confirmFont={confirmFont}
          />
        )}

        {step === 'logo-select' && (
          <LogoSelectStep
            allImages={allImages} selectedLogo={selectedLogo} setSelectedLogo={setSelectedLogo}
            confirmLogo={confirmLogo} user={user} novah={novah} pushStep={pushStep}
          />
        )}

        {step === 'logo-alt' && (
          <LogoAltStep
            fileRef={fileRef} logoUrlInput={logoUrlInput} setLogoUrlInput={setLogoUrlInput}
            logoFile={logoFile} setLogoFile={setLogoFile} logoPreview={logoPreview} setLogoPreview={setLogoPreview}
            setSelectedLogo={setSelectedLogo} setAiLogoOrigin={setAiLogoOrigin}
            confirmLogo={confirmLogo} user={user} novah={novah} pushStep={pushStep}
          />
        )}

        {/* AI logo generation — diseña un logo a partir del contexto de marca */}
        {step === 'ai-logo' && (
          <AiLogoGenerator
            brandName={(scanResult ? brandName : manualName) || brandName || manualName}
            colors={scanResultRef.current?.brandColors || scanResult?.brandColors}
            industry={(scanResultRef.current?.brandInfo?.productsServices as string[] | undefined)?.join(', ')}
            description={scanResultRef.current?.brandInfo?.description as string | undefined}
            onLogoReady={handleAiLogoReady}
            onBack={goBack}
          />
        )}

        {(step === 'photo-choice' || step === 'photo-select' || step === 'photo-upload') && (
          <PhotoStep
            allImages={allImages} selectedLogo={selectedLogo}
            selectedPhotos={selectedPhotos} setSelectedPhotos={setSelectedPhotos}
            setUploadedPhotos={setUploadedPhotos} photoFileRef={photoFileRef}
            photoUrlInput={photoUrlInput} setPhotoUrlInput={setPhotoUrlInput}
            addPhotoUrl={addPhotoUrl} confirmPhotos={confirmPhotos}
          />
        )}

        {step === 'template-select' && (
          <TemplateSelectStep
            globalTemplates={globalTemplates} selectedTemplates={selectedTemplates}
            setSelectedTemplates={setSelectedTemplates} confirmTemplates={confirmTemplates}
          />
        )}

        {step === 'content-mix' && (
          <ContentMixStep
            contentMix={contentMix} checkedCategories={checkedCategories}
            toggleCategory={toggleCategory} isCreating={isCreating} handleCreate={handleCreate}
          />
        )}

        {step === 'creating' && <CreatingStep />}

        {step === 'done' && createdWs && (
          <DoneStep createdWs={createdWs} onCreated={onCreated} handleClose={handleClose} />
        )}

        {step === 'manual-name' && (
          <ManualNameStep
            manualName={manualName} setManualName={setManualName} setBrandName={setBrandName}
            user={user} novah={novah} pushStep={pushStep}
          />
        )}

        {step === 'manual-logo' && (
          <ManualLogoStep
            fileRef={fileRef} logoFile={logoFile} setLogoFile={setLogoFile}
            logoPreview={logoPreview} setLogoPreview={setLogoPreview}
            logoUrlInput={logoUrlInput} setLogoUrlInput={setLogoUrlInput}
            setSelectedLogo={setSelectedLogo} setAiLogoOrigin={setAiLogoOrigin}
            goToTemplates={goToTemplates} user={user} novah={novah} pushStep={pushStep}
          />
        )}

        {step === 'document-upload' && (
          <DocumentStep
            docFileRef={docFileRef} docFile={docFile} setDocFile={setDocFile}
            setScanResult={setScanResult} setStep={setStep} goBack={goBack}
            pushStep={pushStep} user={user} novah={novah}
          />
        )}

        {step === 'document-analyzing' && <DocumentAnalyzingStep />}

        {stepHistory.length > 0 && !['greeting', 'scanning', 'creating', 'document-analyzing', 'done'].includes(step) && (
          <BackBtn onClick={goBack}>{'←'} Go back</BackBtn>
        )}
      </InputArea>
    </>
  );

  if (inline) {
    if (!isOpen) return null;
    return chatContent;
  }

  const isBusy = step === 'scanning' || step === 'creating';
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth="540px"
      closeOnOverlayClick={!isBusy}
      closeOnEscape={!isBusy}
    >
      {chatContent}
    </Modal>
  );
}
